import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, CircularProgress, TextField, Switch,
  Dialog, DialogContent, DialogActions, IconButton,
  Select, MenuItem, InputLabel, FormControl, FormControlLabel,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import SearchIcon from '@material-ui/icons/Search';
import PeopleIcon from '@material-ui/icons/People';
import CloseIcon from '@material-ui/icons/Close';
import LaunchIcon from '@material-ui/icons/Launch';
import { COLORS } from '../../theme';
import AdminHint from '../components/AdminHint';
import AdminViewToggle, { useAdminView } from '../components/AdminViewToggle';

// Nível de permissão — o eixo que decide o que a pessoa PODE FAZER (Fase B).
// É este, e não o `role`, que gateia as Preferências do planner.
const PERMISSIONS = {
  viewer: { label: 'Viewer', icon: '👁', bg: '#f1f5f9', color: '#64748b' },
  operator: { label: 'Operador', icon: '⚙', bg: '#eaf2fe', color: '#2b7de9' },
  manager: { label: 'Gestor', icon: '★', bg: '#e9f7ec', color: '#15803d' },
};
const permMeta = (p) => PERMISSIONS[p] || PERMISSIONS.manager;

const API_BASE = '/planner-api';

const useStyles = makeStyles(() => ({
  '@global': {
    '@keyframes slideInUp': {
      '0%': { opacity: 0, transform: 'translateY(20px)' },
      '100%': { opacity: 1, transform: 'translateY(0)' },
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 22,
    background: 'var(--adm-header-bg, #e2f1e9)',
    border: '1px solid var(--adm-header-border, #bfe0d1)',
    borderRadius: 14,
    padding: '18px 22px',
  },
  titleWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
  },
  titleIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: 'rgba(34,197,94,0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: '1.72rem',
    fontWeight: 700,
    color: COLORS.text,
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: '0.78rem',
    color: COLORS.textSecondary,
    marginTop: 2,
  },
  addBtn: {
    background: 'var(--adm-brand, #2f5d50)',
    color: '#fff',
    fontWeight: 600,
    fontSize: '0.82rem',
    borderRadius: 11,
    padding: '9px 22px',
    boxShadow: '0 4px 16px rgba(47,93,80,0.3)',
    textTransform: 'none',
    '&:hover': {
      background: 'var(--adm-brand-dark, #274c41)',
      boxShadow: '0 6px 20px rgba(47,93,80,0.4)',
      transform: 'translateY(-1px)',
    },
    transition: 'all 0.25s ease',
  },
  toolbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 20 },
  cardsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 },
  userCard: {
    position: 'relative', background: 'var(--adm-card, #fff)', borderRadius: 16,
    border: '1px solid var(--adm-card-border, rgba(0,0,0,0.06))',
    boxShadow: '0 1px 2px rgba(20,42,70,0.04), 0 12px 32px rgba(20,42,70,0.07)',
    padding: '18px 20px',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 28px rgba(52,71,103,0.12)' },
  },
  cardActions: { position: 'absolute', top: 14, right: 14, display: 'flex', gap: 2 },
  cardTop: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, paddingRight: 60 },
  cardName: { fontSize: '0.95rem', fontWeight: 700, color: COLORS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardBadges: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 },
  cardOrg: { background: 'var(--adm-section-bg, #f3f8f5)', border: '1px solid var(--adm-section-border, #e2efe8)', borderRadius: 11, padding: '11px 13px' },
  cardOrgLabel: { fontSize: '0.6rem', fontWeight: 700, color: '#6b8a80', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: 'var(--adm-input-bg, #ffffff)',
    border: '1.5px solid var(--adm-input-border, #e2e8f0)',
    borderRadius: 12,
    padding: '0 14px',
    height: 44,
    flex: 1,
    maxWidth: 380,
    boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
    transition: 'all 0.25s ease',
    '&:focus-within': {
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 4px rgba(59,130,246,0.08), 0 1px 3px rgba(0,0,0,0.03)',
    },
  },
  tableCard: {
    background: 'var(--adm-card, #ffffff)',
    borderRadius: 16,
    border: '1px solid var(--adm-card-border, rgba(0,0,0,0.05))',
    boxShadow: '0 1px 2px rgba(20,42,70,0.04), 0 12px 32px rgba(20,42,70,0.07)',
    overflow: 'visible', // necessário para o cabeçalho sticky funcionar (não clipar)
    animation: '$slideInUp 0.4s ease-out',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '14px 20px',
    fontSize: '0.66rem',
    fontWeight: 700,
    color: COLORS.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    borderBottom: '1px solid var(--adm-border, rgba(0,0,0,0.06))',
    background: 'var(--adm-thead-bg, #f8fafc)',
    position: 'sticky', top: 0, zIndex: 2,
  },
  td: {
    padding: '15px 20px',
    fontSize: '0.84rem',
    color: COLORS.text,
    borderBottom: '1px solid var(--adm-border, rgba(0,0,0,0.03))',
  },
  link: {
    color: '#15803d', cursor: 'pointer', fontWeight: 600,
    textDecorationColor: 'rgba(21,128,61,0.35)',
    '&:hover': { textDecoration: 'underline' },
  },
  tableRow: {
    transition: 'background 0.15s',
    '&:hover': { background: 'var(--adm-hover, #f8fafc)' },
    '&:last-child td': { borderBottom: 'none' },
  },
  userCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  userAvatar: {
    width: 34,
    height: 34,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '0.72rem',
    color: '#fff',
    flexShrink: 0,
  },
  badge: {
    display: 'inline-block',
    padding: '4px 11px',
    borderRadius: 6,
    fontSize: '0.66rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  badgeActive: {
    background: 'rgba(34,197,94,0.08)',
    color: '#16a34a',
  },
  badgeInactive: {
    background: 'rgba(239,68,68,0.08)',
    color: '#dc2626',
  },
  roleBadge: {
    display: 'inline-block',
    padding: '4px 11px',
    borderRadius: 6,
    fontSize: '0.66rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  actionBtn: {
    color: 'var(--adm-text-muted, #94a3b8)',
    transition: 'all 0.2s',
    '&:hover': { color: '#3b82f6', background: 'rgba(59,130,246,0.06)' },
  },
  deleteBtn: {
    color: 'var(--adm-text-muted, #94a3b8)',
    transition: 'all 0.2s',
    '&:hover': { color: 'var(--adm-danger, #ef4444)', background: 'rgba(239,68,68,0.06)' },
  },
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 20,
      padding: '8px 4px',
      width: 760,
      maxWidth: '94vw',
      boxShadow: '0 24px 64px rgba(0,0,0,0.15)',
    },
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 28px 12px',
  },
  dialogTitle: {
    fontWeight: 700,
    fontSize: '1.15rem',
    color: COLORS.text,
  },
  dialogContent: {
    padding: '12px 28px 20px',
  },
  formGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 14,
  },
  dialogActions: {
    padding: '12px 28px 20px',
    gap: 10,
  },
  cancelBtn: {
    color: COLORS.textMuted,
    fontWeight: 500,
    borderRadius: 10,
    padding: '8px 20px',
    '&:hover': { background: 'var(--adm-hover, #f1f5f9)' },
  },
  saveBtn: {
    background: 'var(--adm-brand, #2f5d50)',
    color: '#fff',
    fontWeight: 600,
    borderRadius: 10,
    padding: '8px 28px',
    boxShadow: '0 4px 12px rgba(47,93,80,0.3)',
    '&:hover': {
      background: 'var(--adm-brand-dark, #274c41)',
    },
    '&:disabled': {
      background: '#e2e8f0',
      color: '#94a3b8',
      boxShadow: 'none',
    },
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 300,
  },
  emptyRow: {
    textAlign: 'center',
    padding: 40,
    color: COLORS.textMuted,
    fontSize: '0.85rem',
  },
}));

const ROLE_COLORS = {
  super_admin: { bg: 'rgba(239,68,68,0.08)', color: '#dc2626' },
  admin: { bg: 'rgba(59,130,246,0.08)', color: '#3b82f6' },
  user: { bg: 'rgba(99,102,241,0.08)', color: '#6366f1' },
};

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  user: 'Utilizador',
};

const AVATAR_COLORS = ['#3b82f6', '#22c55e', '#6366f1', '#f59e0b', '#ec4899', '#ef4444', '#14b8a6', '#8b5cf6'];

const EMPTY_USER = { name: '', email: '', password: '', role: 'user', clientId: '', clientLocked: false, language: 'pt', isActive: true, profileId: '', permissionLevel: 'manager', zoneId: '', dataScope: 'org', managedClientIds: [] };

function AdminUsers({ token, navParams, onNavigate, currentUser }) {
  // O admin ATUAL é "não-restrito" (vê tudo) quando não tem managedClientIds.
  // Só ele pode ver/definir o ÂMBITO de outros super_admins. Um admin restrito
  // nunca vê o campo de âmbito nem a opção "Super Admin" — assim não percebe que
  // existem outros clientes.
  const isUnrestrictedAdmin = !Array.isArray(currentUser?.managedClientIds) || currentUser.managedClientIds.length === 0;
  const classes = useStyles();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [view, setView] = useAdminView('users');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_USER);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      const [uRes, cRes, pRes, zRes] = await Promise.all([
        fetch(`${API_BASE}/users`, { headers }),
        fetch(`${API_BASE}/clients`, { headers }),
        fetch(`${API_BASE}/profiles`, { headers }),
        fetch(`${API_BASE}/zones`, { headers }),
      ]);
      if (uRes.ok) setUsers(await uRes.json());
      if (cRes.ok) setClients(await cRes.json());
      if (pRes.ok) setProfiles(await pRes.json());
      if (zRes.ok) setZones(await zRes.json());
    } catch (err) {
      console.error('Users load error:', err);
    }
    setLoading(false);
  }

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_USER);
    setError('');
    setDialogOpen(true);
  }

  function openEdit(user) {
    setEditing(user);
    setForm({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      clientId: user.clientId || '',
      clientLocked: !!user.clientLocked,
      language: user.language || 'pt',
      isActive: user.isActive,
      profileId: user.profileId || '',
      permissionLevel: user.permissionLevel || 'manager',
      zoneId: user.zoneId || '',
      dataScope: user.dataScope || 'org',
      managedClientIds: Array.isArray(user.managedClientIds) ? user.managedClientIds : [],
    });
    setError('');
    setDialogOpen(true);
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const body = {
        name: form.name,
        email: form.email,
        role: form.role,
        clientId: form.clientId || null,
        clientLocked: !!form.clientLocked,
        language: form.language,
        isActive: form.isActive,
        profileId: form.profileId || null,
        permissionLevel: form.permissionLevel || 'manager',
        zoneId: form.zoneId || null,
        dataScope: form.dataScope || 'org',
      };

      // Âmbito de backoffice: só o super_admin NÃO-restrito o define, e só para
      // utilizadores super_admin. Vazio = sem restrição (vê tudo).
      if (isUnrestrictedAdmin && form.role === 'super_admin') {
        body.managedClientIds = Array.isArray(form.managedClientIds) ? form.managedClientIds : [];
      }

      if (form.password) body.password = form.password;

      let res;
      if (editing) {
        res = await fetch(`${API_BASE}/users/${editing.id}`, {
          method: 'PUT', headers, body: JSON.stringify(body),
        });
      } else {
        if (!form.password) {
          setError('Password e obrigatoria para novos utilizadores');
          setSaving(false);
          return;
        }
        body.password = form.password;
        res = await fetch(`${API_BASE}/users`, {
          method: 'POST', headers, body: JSON.stringify(body),
        });
      }

      if (!res.ok) {
        const data = await res.json();
        setError(data.error || 'Erro ao guardar');
        setSaving(false);
        return;
      }

      setDialogOpen(false);
      setLoading(true);
      await loadData();
    } catch (err) {
      console.error('Save error:', err);
      setError('Erro de ligacao');
    }
    setSaving(false);
  }

  async function handleDelete(user) {
    if (!window.confirm(`Tem a certeza que deseja apagar o utilizador "${user.name}"?`)) return;
    try {
      const res = await fetch(`${API_BASE}/users/${user.id}`, { method: 'DELETE', headers });
      if (res.ok) {
        setLoading(true);
        await loadData();
      } else {
        const data = await res.json();
        alert(data.error || 'Erro ao apagar');
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
  }

  // "Entrar como" este utilizador: pede ao backend um token de sessão do planner
  // e abre o planner numa tab nova já autenticado como ele (como se a sessão
  // viesse do EcooGPS). Só o super_admin o consegue; o backend valida o âmbito.
  async function handleImpersonate(user) {
    try {
      const res = await fetch(`${API_BASE}/auth/impersonate`, {
        method: 'POST', headers, body: JSON.stringify({ userId: user.id }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Não foi possível entrar como este utilizador');
        return;
      }
      const data = await res.json();
      const url = `${window.location.origin}/?impersonate=${encodeURIComponent(data.token)}`;
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      console.error('Impersonate error:', err);
      alert('Erro ao entrar como utilizador');
    }
  }

  function getInitials(name) {
    if (!name) return '?';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  }

  const filtered = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase()) ||
    (u.client?.name || '').toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <Box className={classes.loading}><CircularProgress size={36} style={{ color: '#3b82f6' }} /></Box>;
  }

  return (
    <Box>
      <Box className={classes.header}>
        <Box className={classes.titleWrap}>
          <Box className={classes.titleIcon}>
            <PeopleIcon style={{ color: '#22c55e', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography className={classes.title}>Utilizadores</Typography>
            <Typography className={classes.subtitle}>{users.length} utilizadores registados na plataforma</Typography>
          </Box>
        </Box>
        <Button className={classes.addBtn} startIcon={<AddIcon />} onClick={openCreate} disableElevation>
          Novo Utilizador
        </Button>
      </Box>

      <AdminHint
        nodes={[{ text: 'Utilizador', tone: 'teal' }, { text: 'Organização', tone: 'green' }, { text: 'Perfil', tone: 'indigo' }]}
        labels={['pertence a', 'usa o Perfil dela']}
        note="O Utilizador pertence a uma Organização e usa o Perfil dela. Aqui defines: o papel, a permissão (só leitura / operador / gestor — é ela que decide o que pode fazer), a zona + âmbito de dados (o que ele vê) e um perfil add-on opcional (módulos extra)."
      />

      <Box className={classes.toolbar}>
        <Box className={classes.searchBox}>
          <SearchIcon style={{ color: 'var(--adm-text-muted, #94a3b8)', fontSize: 19 }} />
          <input
            style={{ border: 'none', outline: 'none', flex: 1, fontSize: '0.85rem', color: COLORS.text, background: 'transparent' }}
            placeholder="Pesquisar por nome, email ou cliente..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </Box>
        <AdminViewToggle view={view} onChange={setView} />
      </Box>

      {filtered.length === 0 ? (
        <Box className={classes.tableCard}>
          <Typography className={classes.emptyRow}>Nenhum utilizador encontrado</Typography>
        </Box>
      ) : view === 'table' ? (
        <Box className={classes.tableCard}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={classes.th}>Utilizador</th>
                <th className={classes.th}>Papel</th>
                <th className={classes.th}>Permissão</th>
                <th className={classes.th}>Entidade / Organização</th>
                <th className={classes.th}>Estado</th>
                <th className={classes.th} style={{ width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((user, idx) => {
                const rc = ROLE_COLORS[user.role] || ROLE_COLORS.user;
                const pm = permMeta(user.permissionLevel);
                return (
                  <tr key={user.id} className={classes.tableRow}>
                    <td className={classes.td}>
                      <Box className={classes.userCell}>
                        <Box className={classes.userAvatar} style={{ background: AVATAR_COLORS[idx % AVATAR_COLORS.length] }}>
                          {getInitials(user.name)}
                        </Box>
                        <Box>
                          <Typography style={{ fontWeight: 600, fontSize: '0.85rem', color: COLORS.text }}>{user.name}</Typography>
                          <Typography style={{ fontSize: '0.7rem', color: COLORS.textMuted }}>{user.email}</Typography>
                        </Box>
                      </Box>
                    </td>
                    <td className={classes.td}>
                      <span className={classes.roleBadge} style={{ background: rc.bg, color: rc.color }}>
                        {ROLE_LABELS[user.role] || user.role}
                      </span>
                    </td>
                    <td className={classes.td}>
                      <span className={classes.roleBadge} style={{ background: pm.bg, color: pm.color }}>{pm.icon} {pm.label}</span>
                    </td>
                    <td className={classes.td}>
                      <Typography component="div" style={{ fontSize: '0.84rem', color: user.client ? COLORS.text : COLORS.textMuted, display: 'flex', alignItems: 'center', gap: 6 }}>
                        {user.clientLocked && <span title="Associação fixa (login respeita)" style={{ fontSize: 12 }}>🔒</span>}
                        {user.clientId && onNavigate
                          ? <span className={classes.link} title="Abrir a Entidade/Organização"
                              onClick={() => onNavigate('clients', { editClientId: user.clientId })}>
                              {user.client?.name || '-'}
                            </span>
                          : (user.client?.name || '-')}
                        {user.client?.group?.name && <span style={{ fontSize: '0.68rem', color: COLORS.textMuted }}>· {user.client.group.name}</span>}
                      </Typography>
                    </td>
                    <td className={classes.td}>
                      <span className={`${classes.badge} ${user.isActive ? classes.badgeActive : classes.badgeInactive}`}>
                        {user.isActive ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className={classes.td}>
                      <Box style={{ display: 'flex', gap: 2 }}>
                        <IconButton size="small" title="Entrar no planner como este utilizador" onClick={() => handleImpersonate(user)} className={classes.actionBtn}>
                          <LaunchIcon style={{ fontSize: 17 }} />
                        </IconButton>
                        <IconButton size="small" onClick={() => openEdit(user)} className={classes.actionBtn}>
                          <EditIcon style={{ fontSize: 17 }} />
                        </IconButton>
                        {user.role !== 'super_admin' && (
                          <IconButton size="small" onClick={() => handleDelete(user)} className={classes.deleteBtn}>
                            <DeleteIcon style={{ fontSize: 17 }} />
                          </IconButton>
                        )}
                      </Box>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      ) : (
        <Box className={classes.cardsGrid}>
          {filtered.map((user, idx) => {
            const rc = ROLE_COLORS[user.role] || ROLE_COLORS.user;
            const pm = permMeta(user.permissionLevel);
            return (
              <Box key={user.id} className={classes.userCard}>
                <Box className={classes.cardActions}>
                  <IconButton size="small" title="Entrar no planner como este utilizador" onClick={() => handleImpersonate(user)} className={classes.actionBtn}>
                    <LaunchIcon style={{ fontSize: 15 }} />
                  </IconButton>
                  <IconButton size="small" onClick={() => openEdit(user)} className={classes.actionBtn}>
                    <EditIcon style={{ fontSize: 15 }} />
                  </IconButton>
                  {user.role !== 'super_admin' && (
                    <IconButton size="small" onClick={() => handleDelete(user)} className={classes.deleteBtn}>
                      <DeleteIcon style={{ fontSize: 15 }} />
                    </IconButton>
                  )}
                </Box>
                <Box className={classes.cardTop}>
                  <Box className={classes.userAvatar} style={{ background: AVATAR_COLORS[idx % AVATAR_COLORS.length], width: 42, height: 42 }}>
                    {getInitials(user.name)}
                  </Box>
                  <Box style={{ minWidth: 0, flex: 1 }}>
                    <Typography className={classes.cardName}>{user.name}</Typography>
                    <Typography style={{ fontSize: '0.7rem', color: COLORS.textMuted, overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.email}</Typography>
                  </Box>
                </Box>
                <Box className={classes.cardBadges}>
                  <span className={classes.roleBadge} style={{ background: rc.bg, color: rc.color }}>{ROLE_LABELS[user.role] || user.role}</span>
                  <span className={classes.roleBadge} style={{ background: pm.bg, color: pm.color }}>{pm.icon} {pm.label}</span>
                  <span className={`${classes.badge} ${user.isActive ? classes.badgeActive : classes.badgeInactive}`}>
                    {user.isActive ? 'Ativo' : 'Inativo'}
                  </span>
                </Box>
                <Box className={classes.cardOrg}>
                  <Typography className={classes.cardOrgLabel}>Entidade / Organização</Typography>
                  <Typography style={{ fontSize: '0.85rem', fontWeight: 700, color: user.client ? COLORS.text : COLORS.textMuted, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {user.clientLocked && <span title="Associação fixa" style={{ fontSize: 12 }}>🔒</span>}
                    {user.client?.name || '— sem entidade/organização —'}
                  </Typography>
                  {user.client?.group?.name && (
                    <Typography style={{ fontSize: '0.68rem', color: 'var(--adm-brand-mid, #5a8577)', marginTop: 2 }}>Grupo {user.client.group.name}</Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} className={classes.dialog} maxWidth={false}>
        <Box className={classes.dialogHeader}>
          <Typography className={classes.dialogTitle}>
            {editing ? 'Editar Utilizador' : 'Novo Utilizador'}
          </Typography>
          <IconButton size="small" onClick={() => setDialogOpen(false)} style={{ color: 'var(--adm-text-muted, #94a3b8)' }}>
            <CloseIcon style={{ fontSize: 20 }} />
          </IconButton>
        </Box>
        <DialogContent className={classes.dialogContent}>
          <Box className={classes.formGrid}>
            <Box className={classes.formRow}>
              <TextField
                label="Nome" fullWidth variant="outlined" size="small"
                value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              />
              <TextField
                label="Email" fullWidth variant="outlined" size="small" type="email"
                value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
              />
            </Box>
            <TextField
              label={editing ? 'Nova Password (deixar vazio para manter)' : 'Password'}
              fullWidth variant="outlined" size="small" type="password"
              value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
            />
            <Box className={classes.formRow}>
              <FormControl variant="outlined" size="small">
                <InputLabel>Perfil</InputLabel>
                <Select label="Perfil" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                  <MenuItem value="user">Utilizador</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  {/* "Super Admin" só visível para o admin NÃO-restrito. */}
                  {isUnrestrictedAdmin && <MenuItem value="super_admin">Super Admin</MenuItem>}
                </Select>
              </FormControl>
              {/* Âmbito de backoffice — só o super_admin não-restrito o vê/define,
                  e só para utilizadores super_admin. Vazio = vê TUDO. Selecionados
                  = super_admin restrito a esses clientes. */}
              {isUnrestrictedAdmin && form.role === 'super_admin' && (
                <FormControl variant="outlined" size="small">
                  <InputLabel>Âmbito (clientes que vê)</InputLabel>
                  <Select
                    multiple
                    label="Âmbito (clientes que vê)"
                    value={Array.isArray(form.managedClientIds) ? form.managedClientIds : []}
                    onChange={e => setForm({ ...form, managedClientIds: e.target.value })}
                    renderValue={(sel) => (!sel || sel.length === 0) ? 'Todos (sem restrição)' : `${sel.length} cliente(s)`}
                  >
                    {clients.map(c => (
                      <MenuItem key={c.id} value={c.id}>{c.ecoogpsClientName || c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <FormControl variant="outlined" size="small">
                <InputLabel>Entidade / Organização</InputLabel>
                <Select
                  label="Entidade / Organização"
                  value={form.clientId}
                  onChange={e => setForm({ ...form, clientId: e.target.value })}
                >
                  <MenuItem value="">Nenhum (Super Admin)</MenuItem>
                  {clients.map(c => {
                    // Mostra o nome REAL da empresa (client_details) quando já
                    // existe; senão o nome do registo (derivado do user).
                    const label = c.ecoogpsClientName || c.name;
                    const sub = c.ecoogpsClientName ? ` (user: ${c.name})` : '';
                    return (
                      <MenuItem key={c.id} value={c.id}>
                        {label}{c.group?.name ? ` · ${c.group.name}` : ''}{sub}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box className={classes.formRow}>
              {/* Perfil add-on — aditivo SÓ de módulos/acessos extra para esta pessoa */}
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Perfil add-on (módulos extra)</InputLabel>
                <Select
                  label="Perfil add-on (módulos extra)"
                  value={form.profileId || ''}
                  onChange={e => setForm({ ...form, profileId: e.target.value })}
                >
                  <MenuItem value=""><em>— nenhum —</em></MenuItem>
                  {profiles.filter(p => p.scope === 'user_addon').map(p => (
                    <MenuItem key={p.id} value={p.id}>{p.name} ({(p.moduleKeys || []).length} módulos)</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* Nível de permissão (Fase B) — o que a pessoa pode FAZER na app */}
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Permissão</InputLabel>
                <Select
                  label="Permissão"
                  value={form.permissionLevel || 'manager'}
                  onChange={e => setForm({ ...form, permissionLevel: e.target.value })}
                >
                  <MenuItem value="viewer">Só leitura (viewer) — vê, não altera</MenuItem>
                  <MenuItem value="operator">Operador — cria OTs, rotas, favoritos</MenuItem>
                  <MenuItem value="manager">Gestor — tudo + preferências</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {/* Fase A — âmbito de visibilidade + zona */}
            <Box style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <FormControl variant="outlined" size="small" style={{ flex: 1 }}>
                <InputLabel>Âmbito de dados</InputLabel>
                <Select
                  label="Âmbito de dados"
                  value={form.dataScope || 'org'}
                  onChange={e => setForm({ ...form, dataScope: e.target.value })}
                >
                  <MenuItem value="org">Toda a Entidade/Organização</MenuItem>
                  <MenuItem value="zone">Só a sua Zona</MenuItem>
                  <MenuItem value="own">Só os seus registos</MenuItem>
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" style={{ flex: 1 }} disabled={form.dataScope !== 'zone'}>
                <InputLabel>Zona</InputLabel>
                <Select
                  label="Zona"
                  value={form.zoneId || ''}
                  onChange={e => setForm({ ...form, zoneId: e.target.value })}
                >
                  <MenuItem value=""><em>— sem zona —</em></MenuItem>
                  {zones.filter(z => String(z.clientId) === String(form.clientId)).map(z => (
                    <MenuItem key={z.id} value={z.id}>{z.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {/* Dica: cliente real no EcooGPS (client_details) do cliente escolhido */}
            {(() => {
              const sel = clients.find(c => String(c.id) === String(form.clientId));
              if (!sel?.ecoogpsClientName) return null;
              return (
                <Typography style={{ fontSize: '0.72rem', color: 'var(--adm-text-muted, #94a3b8)', marginTop: -4 }}>
                  EcooGPS: {sel.ecoogpsClientName} (client {sel.ecoogpsClientId})
                </Typography>
              );
            })()}
            {/* Cadeado: associação autoritária — o login respeita e não re-mapeia */}
            <FormControlLabel
              control={<Switch checked={!!form.clientLocked} onChange={e => setForm({ ...form, clientLocked: e.target.checked })} color="primary" />}
              label={<Box>
                <Typography style={{ fontSize: '0.88rem', fontWeight: 500 }}>Fixar cliente (associação autoritária) 🔒</Typography>
                <Typography style={{ fontSize: '0.7rem', color: 'var(--adm-text-muted, #94a3b8)' }}>Com isto ligado, o login respeita este cliente e NÃO re-mapeia pelo id EcooGPS (corrige users mal configurados no EcooGPS).</Typography>
              </Box>}
            />
            <FormControlLabel
              control={<Switch checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} color="primary" />}
              label={<Typography style={{ fontSize: '0.88rem', fontWeight: 500 }}>Utilizador ativo</Typography>}
            />
            {error && (
              <Typography style={{ fontSize: '0.82rem', color: 'var(--adm-danger, #ef4444)', fontWeight: 500 }}>{error}</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setDialogOpen(false)} className={classes.cancelBtn}>Cancelar</Button>
          <Button
            onClick={handleSave} variant="contained" className={classes.saveBtn} disableElevation
            disabled={saving || !form.name || !form.email || (!editing && !form.password)}
          >
            {saving ? <CircularProgress size={18} style={{ color: '#fff' }} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminUsers;
